import { useMemo, useState, type FormEvent } from "react";
import { useEditorStore, type ExternalEndpointCreate } from "../app/store";
import { Icon } from "../components/Icon";
import type { ExternalEndpointNode } from "../domain/patch";
import { defaultExternalPosition, externalEndpoints } from "./workspace-ports";

type EndpointDraft = {
  deviceName: string;
  portName: string;
  direction: ExternalEndpointNode["direction"];
  signalType: ExternalEndpointNode["signalType"];
  connectorType: NonNullable<ExternalEndpointNode["connectorType"]>;
  description: string;
  voltageNotes: string;
};

const emptyDraft: EndpointDraft = {
  deviceName: "",
  portName: "",
  direction: "output",
  signalType: "cv",
  connectorType: "3.5mm-ts",
  description: "",
  voltageNotes: "",
};

function draftFromEndpoint(endpoint: ExternalEndpointNode): EndpointDraft {
  return {
    deviceName: endpoint.deviceName ?? endpoint.displayName,
    portName: endpoint.portName ?? "",
    direction: endpoint.direction,
    signalType: endpoint.signalType,
    connectorType: endpoint.connectorType ?? "3.5mm-ts",
    description: endpoint.description ?? "",
    voltageNotes: endpoint.voltageNotes ?? "",
  };
}

function EndpointForm({
  endpoint,
  index,
  onDone,
}: {
  endpoint: ExternalEndpointNode | null;
  index: number;
  onDone: () => void;
}) {
  const activeDocument = useEditorStore((state) => state.activeDocument);
  const createEndpoint = useEditorStore((state) => state.createExternalEndpoint);
  const updateEndpoint = useEditorStore((state) => state.updateExternalEndpoint);
  const deleteEndpoint = useEditorStore((state) => state.deleteExternalEndpoint);
  const [draft, setDraft] = useState<EndpointDraft>(
    endpoint ? draftFromEndpoint(endpoint) : emptyDraft,
  );
  const valid = Boolean(draft.deviceName.trim() && draft.portName.trim());

  const submit = (event: FormEvent) => {
    event.preventDefault();
    if (!valid) return;
    const deviceName = draft.deviceName.trim();
    const portName = draft.portName.trim();
    const value: ExternalEndpointCreate = {
      displayName: `${deviceName}: ${portName}`,
      deviceName,
      portName,
      direction: draft.direction,
      signalType: draft.signalType,
      connectorType: draft.connectorType,
      position: endpoint?.position ?? defaultExternalPosition(index, activeDocument),
      edgeDock: "bottom",
      description: draft.description.trim(),
      voltageNotes: draft.voltageNotes.trim(),
    };
    if (endpoint) updateEndpoint(endpoint.id, value);
    else createEndpoint(value);
    onDone();
  };

  const field = <K extends keyof EndpointDraft>(key: K, value: EndpointDraft[K]) =>
    setDraft((current) => ({ ...current, [key]: value }));

  return (
    <form className="external-endpoint-form" onSubmit={submit}>
      <label>
        <span>Device</span>
        <input
          autoFocus
          onChange={(event) => field("deviceName", event.target.value)}
          placeholder="e.g. Audio interface"
          value={draft.deviceName}
        />
      </label>
      <label>
        <span>Port</span>
        <input
          onChange={(event) => field("portName", event.target.value)}
          placeholder="e.g. Input 3"
          value={draft.portName}
        />
      </label>
      <div className="external-endpoint-form-grid">
        <label>
          <span>Direction</span>
          <select
            onChange={(event) =>
              field("direction", event.target.value as EndpointDraft["direction"])
            }
            value={draft.direction}
          >
            <option value="input">Input</option>
            <option value="output">Output</option>
            <option value="bidirectional">Bidirectional</option>
          </select>
        </label>
        <label>
          <span>Signal</span>
          <select
            onChange={(event) =>
              field("signalType", event.target.value as EndpointDraft["signalType"])
            }
            value={draft.signalType}
          >
            <option value="audio">Audio</option>
            <option value="cv">CV</option>
            <option value="gate">Gate</option>
            <option value="clock">Clock</option>
            <option value="expression">Expression</option>
            <option value="mixed">Audio / CV</option>
            <option value="unknown">Other / unknown</option>
          </select>
        </label>
      </div>
      <label>
        <span>Connector</span>
        <select
          onChange={(event) =>
            field("connectorType", event.target.value as EndpointDraft["connectorType"])
          }
          value={draft.connectorType}
        >
          <option value="3.5mm-ts">3.5 mm TS</option>
          <option value="6.35mm-ts">6.35 mm TS</option>
          <option value="trs">TRS</option>
          <option value="din-midi">DIN MIDI</option>
          <option value="usb">USB</option>
          <option value="other">Other</option>
        </select>
      </label>
      <label>
        <span>Voltage / level notes</span>
        <input
          onChange={(event) => field("voltageNotes", event.target.value)}
          placeholder="e.g. 0–5 V"
          value={draft.voltageNotes}
        />
      </label>
      <label>
        <span>Description</span>
        <textarea
          onChange={(event) => field("description", event.target.value)}
          placeholder="Purpose in this patch"
          rows={2}
          value={draft.description}
        />
      </label>
      <div className="external-endpoint-form-actions">
        {endpoint && (
          <button
            className="is-danger"
            onClick={() => {
              deleteEndpoint(endpoint.id);
              onDone();
            }}
            type="button"
          >
            <Icon name="trash" size={14} /> Delete
          </button>
        )}
        <button onClick={onDone} type="button">
          Cancel
        </button>
        <button disabled={!valid} type="submit">
          <Icon name={endpoint ? "save" : "plus"} size={14} />
          {endpoint ? "Save endpoint" : "Add endpoint"}
        </button>
      </div>
    </form>
  );
}

export function ExternalEndpointManager() {
  const activeDocument = useEditorStore((state) => state.activeDocument);
  const select = useEditorStore((state) => state.select);
  const [open, setOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | "new" | null>(null);
  const endpoints = useMemo(() => externalEndpoints(activeDocument), [activeDocument]);
  const editingEndpoint =
    editingId && editingId !== "new"
      ? (endpoints.find((endpoint) => endpoint.id === editingId) ?? null)
      : null;

  return (
    <section
      aria-label="External equipment"
      className={`external-endpoint-manager${open ? " is-open" : ""}`}
    >
      <button
        aria-expanded={open}
        className="external-endpoint-toggle"
        onClick={() => setOpen((current) => !current)}
        type="button"
      >
        <Icon name="cable" size={16} />
        <span>External</span>
        <b>{endpoints.length}</b>
        <Icon name="chevron" size={14} />
      </button>
      {open && (
        <div className="external-endpoint-body">
          <header>
            <div>
              <span className="eyebrow">Workspace</span>
              <h2>External equipment</h2>
            </div>
            <div>
              <button
                aria-label="Add external endpoint"
                onClick={() => setEditingId("new")}
                type="button"
              >
                <Icon name="plus" size={15} />
              </button>
              <button
                aria-label="Close external equipment"
                onClick={() => setOpen(false)}
                type="button"
              >
                <Icon name="close" size={15} />
              </button>
            </div>
          </header>
          {editingId ? (
            <EndpointForm
              endpoint={editingEndpoint}
              index={endpoints.length}
              key={editingEndpoint?.id ?? "new"}
              onDone={() => setEditingId(null)}
            />
          ) : endpoints.length ? (
            <ul className="external-endpoint-list">
              {endpoints.map((endpoint) => (
                <li key={endpoint.id}>
                  <button
                    onClick={() => {
                      select(endpoint.id);
                      setEditingId(endpoint.id);
                    }}
                    type="button"
                  >
                    <span>
                      <strong>{endpoint.deviceName ?? endpoint.displayName}</strong>
                      <small>{endpoint.portName ?? endpoint.displayName}</small>
                    </span>
                    <em>
                      {endpoint.direction} · {endpoint.signalType}
                    </em>
                  </button>
                </li>
              ))}
            </ul>
          ) : (
            <div className="external-endpoint-empty">
              <p>Document ports on interfaces, modular gear, clock sources, and other equipment.</p>
              <button onClick={() => setEditingId("new")} type="button">
                <Icon name="plus" size={14} /> Add endpoint
              </button>
            </div>
          )}
        </div>
      )}
    </section>
  );
}
